import { t } from './i18n.js';
let mailboxId = '';
export function setMailboxId(id: string) {
  mailboxId = id;
}
export function mailboxUrl(path: string) {
  return mailboxId
    ? path + (path.includes('?') ? '&' : '?') + 'mailboxId=' + encodeURIComponent(mailboxId)
    : path;
}
let csrfToken = '';
// Called when an authenticated request is refused (expired or revoked session).
let unauthorized: (() => void) | undefined;
export function onUnauthorized(handler?: () => void) {
  unauthorized = handler;
}
export function setCsrf(value: string) {
  csrfToken = value;
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: any,
  ) {
    super(message);
  }
}
export async function api<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/v1${path}`, {
      ...options,
      credentials: 'same-origin',
      headers: {
        ...(options.body && typeof options.body === 'string'
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(mailboxId ? { 'X-Mailbox-Id': mailboxId } : {}),
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        ...options.headers,
      },
    });
  } catch {
    throw new ApiError(t('Connexion interrompue. Votre texte reste dans cet onglet.'), 0);
  }
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith('/auth/')) unauthorized?.();
    const body = await response
      .json()
      .catch(() => ({ detail: 'Le service est momentanément indisponible.' }));
    // The server answers in French and that text is the translation key, so every message of the
    // catalogue reaches the reader in the language they chose, values included.
    throw new ApiError(
      t(body.detail ?? 'Impossible de terminer cette action.', body.params),
      response.status,
      body,
    );
  }
  return response.json();
}
export const send = <T = any>(
  path: string,
  body: any,
  method = 'POST',
  headers: Record<string, string> = {},
) => api<T>(path, { method, body: JSON.stringify(body), headers });
export function uploadFile(file: File, onProgress: (progress: number) => void): Promise<any> {
  return new Promise((resolve, reject) => {
    const req = new XMLHttpRequest();
    req.open('POST', '/v1/uploads');
    req.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    req.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
    req.setRequestHeader('X-CSRF-Token', csrfToken);
    if (mailboxId) req.setRequestHeader('X-Mailbox-Id', mailboxId);
    req.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    req.onload = () => {
      let body: any;
      try {
        body = JSON.parse(req.responseText);
      } catch {
        reject(new Error(t('Réponse de téléversement invalide.')));
        return;
      }
      if (req.status >= 200 && req.status < 300) resolve(body);
      else {
        if (req.status === 401) unauthorized?.();
        reject(
          new ApiError(t(body.detail ?? 'Téléversement impossible.', body.params), req.status),
        );
      }
    };
    req.onerror = () =>
      reject(new ApiError(t('Connexion interrompue pendant le téléversement.'), 0));
    req.send(file);
  });
}
